@use '@/styles/tokens' as *;

// Figma button variants (node 114-73). Heights/radius/padding/font sizes come
// from antd Button component tokens in src/config/theme.ts; colors live here.
// Double-class selectors out-rank antd's own variant styles on specificity ties.

.button {
  gap: 10px;
  box-shadow: none;
}

.button.highlight {
  background: $color-accent1-500;
  border-color: $color-accent1-500;
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background: $color-accent1-400;
    border-color: $color-accent1-200;
    color: var(--text-primary);
  }
}

.button.primary {
  background: $color-brand-950;
  border-color: $color-brand-950;
  color: $color-accent1-500;

  &:hover:not(:disabled) {
    background: $color-brand-900;
    border-color: $color-brand-800;
    color: $color-accent1-500;
  }
}

.button.neutral {
  background: $color-neutral-950;
  border-color: $color-neutral-950;
  color: var(--text-inverse);

  &:hover:not(:disabled) {
    background: $color-neutral-900;
    border-color: $color-neutral-600;
    color: var(--text-inverse);
  }
}

.button.secondary {
  background: var(--surface-secondary);
  border-color: var(--surface-secondary);
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background: var(--surface-secondary);
    border-color: $color-light-brown-400;
    color: var(--text-primary);
  }
}

.button.subtle {
  background: $color-white-50;
  border-color: $color-white-400;
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background: $color-white-100;
    border-color: $color-white-600;
    color: var(--text-primary);
  }
}

.button.outline {
  background: transparent;
  border-color: $color-white-600;
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background: $color-white-50;
    border-color: $color-white-500;
    color: var(--text-primary);
  }

  &:disabled {
    background: transparent;
    border-color: $color-white-500;
  }
}

.icon {
  display: inline-flex;

  > svg {
    width: 14px;
    height: 14px;
  }
}

.button:global(.ant-btn-lg) .icon > svg {
  width: 16px;
  height: 16px;
}

.button:global(.ant-btn-sm) .icon > svg {
  width: 12px;
  height: 12px;
}

/* A control that must look unavailable and still take a click: `Save Mapping` below three Points
 * has to say "At least 3 points are required.", and a real `disabled` attribute dispatches no click
 * event at all. Same three colours as `:disabled` — nothing new is drawn.
 *
 * Base rule: `.button[aria-disabled='true']` is 1 class + 1 attribute = (0,2,0), which TIES each
 * variant's own base rule (e.g. `.button.highlight` = 2 classes = (0,2,0)) — source order correctly
 * decides that tie because this block is LAST in the file.
 *
 * Hover rule needs its own fix, not a tie-break: every variant's `&:hover:not(:disabled)` compiles to
 * e.g. `.button.highlight:hover:not(:disabled)` = 2 classes + :hover + :not(:disabled) (the :not()
 * argument's own specificity counts) = 2 classes + 2 pseudo-classes = (0,4,0). A plain
 * `.button[aria-disabled='true']:hover` is only 1 class + 1 attribute + :hover = (0,3,0), which LOSES
 * outright — no tie, no source-order rescue — so a hovered visually-disabled button reverted to its
 * variant's active hover colour. Fix: `.button.button[aria-disabled='true']:hover:not(:disabled)`
 * repeats the class to 2 classes, keeps the 1 attribute, and counts 2 pseudo-classes (:hover plus
 * :not(:disabled)'s own :disabled) — 2 + 1 + 2 = (0,5,0), which strictly beats (0,4,0) — always, for
 * every variant, regardless of source order. */
// `:disabled` lives here too, for the same reason: as `.button:disabled` (0,2,0) at the top of the
// file it tied every variant's base rule and lost on source order, so a disabled neutral/primary/
// highlight button kept its enabled colours. `.button.outline:disabled` (0,3,0) still wins.
.button:disabled,
.button[aria-disabled='true'],
.button.button[aria-disabled='true']:hover:not(:disabled) {
  background: var(--surface-muted);
  border-color: var(--surface-muted);
  color: var(--overlay-20);
  cursor: not-allowed;
}
